import { z } from 'zod';
import { IsoDate, IsoDateTime, Uuid } from './common.js';
import { SupplierDecisionData } from './support.js';

/**
 * Lot BG « Mails des fournisseurs » (contrat du 20/10/2026, CDC « Tri des fournisseurs ») : vue Fournisseurs de
 * l'onglet Support. Routes `GET /api/support/suppliers`, `PATCH /api/support/suppliers/:id`,
 * `POST /api/support/suppliers/decisions`, `PATCH /api/support/suppliers/decisions/:id`.
 */

/** Croisement d'un domaine cité avec le back : site en ligne, site pas en ligne, domaine inconnu, back sans domaines. */
export const SupplierCrossing = z.enum(['live', 'not_live', 'unknown_domain', 'unavailable']);
export type SupplierCrossing = z.infer<typeof SupplierCrossing>;

/** Site du back qui porte le domaine cité. */
export const SupplierSite = z.object({
  siteId: z.string(),
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  online: z.boolean(),
});
export type SupplierSite = z.infer<typeof SupplierSite>;

/** Un motif important (mails regroupés par motif et domaine cité), avec sa carte « À faire ». */
export const SupplierImportantItem = z.object({
  /** Clé du groupe (motif + domaine). */
  key: z.string(),
  supplier: z.string(),
  /** « OVH · kine-oceane.fr suspendu le 24/10 (NIS2) » (titre de la carte). */
  title: z.string(),
  /** Puce : « Domaine », « Incident », « Paiement »… */
  topic: z.string(),
  reason: z.string().nullable(),
  deadline: IsoDate.nullable(),
  domainName: z.string().nullable(),
  site: SupplierSite.nullable(),
  crossing: SupplierCrossing,
  count: z.int().min(1),
  firstReceivedAt: IsoDateTime,
  lastReceivedAt: IsoDateTime,
  /** Le mail le plus récent du groupe (« Remonter », « Ouvrir le mail »). */
  mailId: Uuid,
  /** Fil Gmail du mail le plus récent (« Ouvrir le mail ↗ »). */
  mailUrl: z.string().nullable(),
  /** Page du domaine chez le fournisseur (« Ouvrir dans OVH ↗ »), null sinon. */
  supplierUrl: z.string().nullable(),
  actionId: Uuid.nullable(),
  actionStatus: z.string().nullable(),
});
export type SupplierImportantItem = z.infer<typeof SupplierImportantItem>;

/** Les mails ignorés d'un même motif (liste « Ignorés », avec compteur et « Remonter »). */
export const SupplierIgnoredGroup = z.object({
  patternKey: z.string(),
  supplier: z.string(),
  /** « OVH · changement de contacts », « Clever Cloud · déploiement réussi ». */
  label: z.string(),
  count: z.int().min(1),
  reason: z.string().nullable(),
  lastReceivedAt: IsoDateTime,
  /** Le mail le plus récent du motif (celui que « Remonter » remet dans la boîte de réception). */
  mailId: Uuid,
  mailUrl: z.string().nullable(),
  /** Exemple d'objet (le plus récent). */
  sampleSubject: z.string(),
  /** Décision de l'équipe qui a fait ignorer ce motif, null sinon. */
  decisionTitle: z.string().nullable(),
  /** Tous archivés dans Gmail (sinon : « Reconnecte ton compte Google pour l'archivage » ou en cours). */
  archived: z.boolean(),
});
export type SupplierIgnoredGroup = z.infer<typeof SupplierIgnoredGroup>;

/** Une décision de l'équipe (`support_memory` kind `supplier_decision`). */
export const SupplierDecisionItem = z.object({
  id: Uuid,
  title: z.string(),
  status: z.enum(['PROPOSED', 'ACTIVE', 'ARCHIVED']),
  data: SupplierDecisionData,
  createdAt: IsoDateTime,
  createdBy: Uuid.nullable(),
  /** Mails triés par cette décision (« 12 rappels ignorés depuis »). */
  appliedCount: z.int().min(0),
});
export type SupplierDecisionItem = z.infer<typeof SupplierDecisionItem>;

/** `GET /api/support/suppliers` */
export const SupplierView = z.object({
  important: z.array(SupplierImportantItem),
  ignored: z.array(SupplierIgnoredGroup),
  /** Mails pas encore triés (Claude indisponible, plafond atteint…). */
  pending: z.int().min(0),
  decisions: z.array(SupplierDecisionItem),
  /** En-tête : mails de fournisseurs reçus ces 7 derniers jours, et leurs fournisseurs. */
  week: z.object({ count: z.int().min(0), suppliers: z.array(z.string()) }),
  /** L'archivage Gmail est autorisé pour toutes les boîtes lues (sinon « Reconnecte ton compte Google… »). */
  archive: z.enum(['ok', 'scope_missing']),
});
export type SupplierView = z.infer<typeof SupplierView>;

/** `PATCH /api/support/suppliers/:id` : « Remonter » (`important`) ou ignorer un mail (`ignored`). */
export const UpdateSupplierMailBody = z.object({ verdict: z.enum(['important', 'ignored']) });
export type UpdateSupplierMailBody = z.infer<typeof UpdateSupplierMailBody>;

export const UpdateSupplierMailResult = z.object({
  id: Uuid,
  verdict: z.enum(['important', 'ignored']),
  actionId: Uuid.nullable(),
  /** Gmail : `ok`, `skipped` (autorisation manquante : rien d'autre ne casse), `none` (rien à faire). */
  gmail: z.enum(['ok', 'skipped', 'none']),
});
export type UpdateSupplierMailResult = z.infer<typeof UpdateSupplierMailResult>;

const DecisionDomain = z
  .string()
  .trim()
  .toLowerCase()
  .transform((d) => d.replace(/^@/, '').replace(/^www\./, ''))
  .pipe(z.string().max(253));

/**
 * `POST /api/support/suppliers/decisions` : décision ACTIVE aussitôt (montrée et vérifiée avant « Retenir ») ; elle
 * re-trie les mails en attente et ceux du dernier mois qu'elle concerne.
 */
export const CreateSupplierDecisionBody = z
  .object({
    /** La phrase de la personne (« le domaine evolusite.net n'est plus utilisé »). */
    title: z.string().trim().min(3).max(200),
    match: z.object({
      senderDomain: DecisionDomain.optional(),
      subjectContains: z.string().trim().max(120).optional(),
      domainName: DecisionDomain.optional(),
    }),
    verdict: z.enum(['ignore', 'important']),
    note: z.string().trim().max(500).optional(),
  })
  .refine((b) => Boolean(b.match.senderDomain || b.match.subjectContains || b.match.domainName), {
    message: 'Indique au moins un critère : expéditeur, mot de l’objet ou domaine cité',
    path: ['match'],
  });
export type CreateSupplierDecisionBody = z.input<typeof CreateSupplierDecisionBody>;

export const CreateSupplierDecisionResult = z.object({
  decision: SupplierDecisionItem,
  /** Mails re-triés par la décision. */
  retriaged: z.int().min(0),
});
export type CreateSupplierDecisionResult = z.infer<typeof CreateSupplierDecisionResult>;

/** `PATCH /api/support/suppliers/decisions/:id` : archiver (ou réactiver) une décision. */
export const UpdateSupplierDecisionBody = z.object({ status: z.enum(['ACTIVE', 'ARCHIVED']) });
export type UpdateSupplierDecisionBody = z.infer<typeof UpdateSupplierDecisionBody>;
